/**
 * Catalog Validation Utilities
 * Validates categories, subcategories, and types against system definitions
 */

import { categories } from './constants';
import { canonicalizeCategoryKey } from '../catalog/legacyAliases';

/**
 * Validates if a category exists in the system
 * @param {string} categoryName - The category name to validate
 * @returns {boolean} - True if category exists
 */
export const isValidCategory = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return false;
  }

  const canonical = canonicalizeCategoryKey(categoryName, categories);
  if (categories[canonical]) {
    return true;
  }

  // Check if it matches any category name (French or Arabic)
  return Object.values(categories).some(category => 
    category.name === categoryName || category.nameAr === categoryName
  );
};

/**
 * Validates if a subcategory exists for a given category
 * @param {string} categoryName - The category name
 * @param {string} subcategoryName - The subcategory name to validate
 * @returns {boolean} - True if subcategory exists for the category
 */
export const isValidSubcategory = (categoryName, subcategoryName) => {
  if (!categoryName || !subcategoryName || typeof categoryName !== 'string' || typeof subcategoryName !== 'string') {
    return false;
  }

  const categoryKey = canonicalizeCategoryKey(categoryName, categories);
  const category =
    categories[categoryKey] ||
    Object.values(categories).find(
      (cat) => cat.name === categoryName || cat.nameAr === categoryName
    );

  if (!category?.subcategories) {
    return false;
  }

  if (category.subcategories[subcategoryName]) {
    return true;
  }

  return Object.values(category.subcategories).some(
    (subcat) => subcat.name === subcategoryName || subcat.nameAr === subcategoryName
  );
};

/**
 * Validates if a type exists for a given category and subcategory
 * @param {string} categoryName - The category name
 * @param {string} subcategoryName - The subcategory name
 * @param {string} typeName - The type name to validate
 * @returns {boolean} - True if type exists for the category/subcategory
 */
export const isValidType = (categoryName, subcategoryName, typeName) => {
  if (!categoryName || !subcategoryName || !typeName || 
      typeof categoryName !== 'string' || typeof subcategoryName !== 'string' || typeof typeName !== 'string') {
    return false;
  }

  const categoryKey = canonicalizeCategoryKey(categoryName, categories);
  const category =
    categories[categoryKey] ||
    Object.values(categories).find(
      (cat) => cat.name === categoryName || cat.nameAr === categoryName
    );

  if (!category?.subcategories) {
    return false;
  }

  const subcategory =
    category.subcategories[subcategoryName] ||
    Object.values(category.subcategories).find(
      (subcat) => subcat.name === subcategoryName || subcat.nameAr === subcategoryName
    );

  if (!subcategory?.types) {
    return false;
  }

  return subcategory.types.some((entry) => {
    if (typeof entry === 'string') return entry === typeName;
    return entry?.key === typeName || entry?.name === typeName || entry?.nameAr === typeName;
  });
};

/**
 * Validates a complete product structure
 * @param {Object} product - The product to validate
 * @returns {Object} - Validation result with errors and warnings
 */
export const validateProduct = (product) => {
  const errors = [];
  const warnings = [];
  
  if (!product) {
    errors.push('Product is required');
    return { isValid: false, errors, warnings };
  }
  
  // Required fields validation
  if (!product.name || product.name.trim() === '') {
    errors.push('Product name is required');
  }
  
  if (!product.sku || product.sku.trim() === '') {
    errors.push('Product SKU is required');
  }
  
  // Category validation
  if (!product.category) {
    errors.push('Product category is required');
  } else if (!isValidCategory(product.category)) {
    errors.push(`Category "${product.category}" does not exist in the system`);
  }
  
  // Subcategory validation
  if (!product.subcategory) {
    errors.push('Product subcategory is required');
  } else if (product.category && !isValidSubcategory(product.category, product.subcategory)) {
    errors.push(`Subcategory "${product.subcategory}" does not exist for category "${product.category}"`);
  }
  
  // Type validation
  if (!product.type) {
    warnings.push('Product type is recommended');
  } else if (product.category && product.subcategory && !isValidType(product.category, product.subcategory, product.type)) {
    warnings.push(`Type "${product.type}" does not exist for category "${product.category}" and subcategory "${product.subcategory}"`);
  }
  
  // Price validation
  if (product.unitPrice && (isNaN(product.unitPrice) || product.unitPrice < 0)) {
    errors.push('Unit price must be a positive number');
  }
  
  if (product.sellingPrice && (isNaN(product.sellingPrice) || product.sellingPrice < 0)) {
    errors.push('Selling price must be a positive number');
  }
  
  // SKU uniqueness validation (this would need to check against existing products)
  if (product.sku && product.sku.length < 3) {
    warnings.push('SKU should be at least 3 characters long');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validates multiple products and returns validation results
 * @param {Array} products - Array of products to validate
 * @returns {Object} - Validation results with valid and invalid products
 */
export const validateProducts = (products) => {
  if (!Array.isArray(products)) {
    return {
      valid: [],
      invalid: [],
      total: 0,
      validCount: 0,
      invalidCount: 0
    };
  }
  
  const valid = [];
  const invalid = [];
  
  products.forEach((product, index) => {
    const validation = validateProduct(product);
    if (validation.isValid) {
      valid.push({ ...product, _validationIndex: index });
    } else {
      invalid.push({ 
        ...product, 
        _validationIndex: index,
        _validationErrors: validation.errors,
        _validationWarnings: validation.warnings
      });
    }
  });
  
  return {
    valid,
    invalid,
    total: products.length,
    validCount: valid.length,
    invalidCount: invalid.length
  };
};

/**
 * Gets available categories for validation
 * @returns {Array} - Array of category objects with key, name, and nameAr
 */
export const getAvailableCategories = () => {
  return Object.entries(categories).map(([key, category]) => ({
    key,
    name: category.name,
    nameAr: category.nameAr
  }));
};

/**
 * Gets available subcategories for a given category
 * @param {string} categoryName - The category name
 * @returns {Array} - Array of subcategory objects with key, name, and nameAr
 */
export const getAvailableSubcategories = (categoryName) => {
  if (!isValidCategory(categoryName)) {
    return [];
  }
  
  // Find the category by name
  const category = Object.values(categories).find(cat => 
    cat.name === categoryName || cat.nameAr === categoryName
  );
  
  if (!category || !category.subcategories) {
    return [];
  }
  
  return Object.entries(category.subcategories).map(([key, subcategory]) => ({
    key,
    name: subcategory.name,
    nameAr: subcategory.nameAr
  }));
};

/**
 * Gets available types for a given category and subcategory
 * @param {string} categoryName - The category name
 * @param {string} subcategoryName - The subcategory name
 * @returns {Array} - Array of type strings
 */
export const getAvailableTypes = (categoryName, subcategoryName) => {
  if (!isValidCategory(categoryName) || !isValidSubcategory(categoryName, subcategoryName)) {
    return [];
  }
  
  // Find the category by name
  const category = Object.values(categories).find(cat => 
    cat.name === categoryName || cat.nameAr === categoryName
  );
  
  if (!category || !category.subcategories) {
    return [];
  }
  
  // Find the subcategory by name
  const subcategory = Object.values(category.subcategories).find(subcat => 
    subcat.name === subcategoryName || subcat.nameAr === subcategoryName
  );
  
  return subcategory?.types || [];
};

/**
 * Maps category and subcategory names to their system keys
 * @param {string} categoryName - The category name
 * @param {string} subcategoryName - The subcategory name
 * @returns {Object} - Object with categoryKey and subcategoryKey
 */
export const mapNamesToKeys = (categoryName, subcategoryName) => {
  if (!categoryName) {
    return { categoryKey: null, subcategoryKey: null };
  }

  const canonical = canonicalizeCategoryKey(categoryName, categories);
  let categoryKey = categories[canonical] ? canonical : null;
  let category = categoryKey ? categories[categoryKey] : null;

  if (!category) {
    const categoryEntry = Object.entries(categories).find(
      ([key, cat]) =>
        key === categoryName ||
        cat.name === categoryName ||
        cat.nameAr === categoryName
    );
    if (!categoryEntry) {
      return { categoryKey: null, subcategoryKey: null };
    }
    [categoryKey, category] = categoryEntry;
  }

  if (!subcategoryName) {
    return { categoryKey, subcategoryKey: null };
  }

  if (category?.subcategories?.[subcategoryName]) {
    return { categoryKey, subcategoryKey: subcategoryName };
  }

  const subcategoryEntry = Object.entries(category.subcategories || {}).find(
    ([, subcategory]) =>
      subcategory.name === subcategoryName || subcategory.nameAr === subcategoryName
  );

  if (!subcategoryEntry) {
    return { categoryKey, subcategoryKey: null };
  }

  return { categoryKey, subcategoryKey: subcategoryEntry[0] };
};

export default {
  isValidCategory,
  isValidSubcategory,
  isValidType,
  validateProduct,
  validateProducts,
  getAvailableCategories,
  getAvailableSubcategories,
  getAvailableTypes,
  mapNamesToKeys
};
